import {Injectable} from '@angular/core';
import {MyPagesRoutesEnum} from '../modules/my-pages/enums/my-pages-routes.enum';
import {QueryParams} from '../types/QueryParams';
import {SearchState} from '../types/SearchState';
import {RoutesEnums, RoutesQueryParamsEnum} from '../enums/RoutesEnums';
import {UrlTermsSymbolsEnum, UrlTermsSymbolTimeslotsEnum} from '../enums/SeoEnums';
import {ToolsService} from './tools.service';
import {PageTemplateEnum} from '../enums/WPEnums';
import {DefaultValuesService} from './default-values.service';
import {RouterData} from '../types/RouterData';
import {AppConfigService} from './app-config.service';

@Injectable({
  providedIn: 'root',
})
export class UrlBuilderService {
  constructor(
    private tools: ToolsService,
    private defaults: DefaultValuesService,
    private appConfigService: AppConfigService,
  ) {}

  public CreateStaticLink(routeLinkTab: string[], queryParams?: QueryParams): string {
    let url: string = routeLinkTab.join('/').replace(/\/\/+/g, '/');
    if (url.slice(0, 1) === '/' && this.appConfigService.appConfig.baseUrl.slice(-1) === '/') {
      url = url.slice(1);
    }
    let queryParamsStr = '';
    if (!this.tools.isNil(queryParams) && Object.keys(queryParams).length > 0) {
      const queryStringCombinedArr: string[] = [];
      Object.keys(queryParams).forEach((key) => {
        queryStringCombinedArr.push(`${key}=${queryParams[key]}`);
      });
      if (queryStringCombinedArr.length > 0) {
        queryParamsStr += `?${queryStringCombinedArr.join('&')}`;
      }
    }
    return this.removeTrailingCharacters(this.appConfigService.appConfig.baseUrl + url + queryParamsStr);
  }

  public removeTrailingCharacters(url: string): string {
    return url.replace(/[?\/]+$/, '');
  }

  public convertUrlToRouterLink(url: string): [string[], QueryParams] {
    const vardenDomain: string = 'https://www.varden.se/';
    if (url && url.includes('https://www.varden.se/')) {
      url = url.replace(vardenDomain, '');
      return this.convertRelativeUrlToRouterLink(url);
    }
    return null;
  }

  public convertRelativeUrlToRouterLink(url: string): [string[], QueryParams] {
    const [base, query = ''] = url.split('?', 2);
    const routerLink = base
      .split('/')
      .filter(Boolean)
      .reduce((acc, part) => [...acc, part], ['/']);
    const queryParams = Object.fromEntries(query.split('&').map((p) => p.split('=')));
    return [routerLink, queryParams];
  }

  public CreateGuideRouterLink(): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.GUIDE);
    return routerLinkTab;
  }

  public CreateGuideTagRouterLink(tag: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.GUIDE);
    routerLinkTab.push(RoutesEnums.TAG);
    routerLinkTab.push(tag);
    return routerLinkTab;
  }

  public CreateGuidePostRouterLink(urlPath: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.GUIDE);
    routerLinkTab.push(urlPath);
    return routerLinkTab;
  }

  public CreateSearchResultsRouterLink(
    searchState: SearchState,
    baseUrl: string[],
    fallbackFilterApplied: boolean = false,
  ): string[] {
    if (this.needUseBaseRoute(searchState, fallbackFilterApplied)) {
      return this.getBaseRouteLink(searchState, baseUrl);
    } else {
      return this.getVardRouteLink(baseUrl);
    }
  }

  public CreateSearchResultsBaseRouterLink(searchState: SearchState, baseUrl: string[]): string[] {
    return this.allParametersCombinations(searchState)
      ? this.getVardRouteLink(baseUrl)
      : this.getBaseRouteLink(searchState, baseUrl);
  }

  public CreateRouteData(searchState: SearchState, baseUrl: string[]): RouterData {
    const defaultSearchState: SearchState = this.defaults.getDefaultSearchState(
      searchState.caregiverUnitTypeUrlPath,
      searchState.careServiceTypeUrlPath,
      searchState.symptomUrlPath,
      searchState.freeSearchTerm,
    );
    const baseRouteQueryParams = this.CreateSearchResultsQueryParams(searchState, false, true);
    const vardRouteQueryParams = this.CreateSearchResultsQueryParams(searchState);

    if (
      !this.tools.isNil(searchState.caregiverUnitTypeUrlPath) &&
      searchState.caregiverUnitTypeUrlPath !== '' &&
      searchState.caregiverUnitTypeUrlPath !== defaultSearchState.caregiverUnitTypeUrlPath
    ) {
      vardRouteQueryParams[UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE] = `${this.NormalizeText(
        searchState.caregiverUnitTypeUrlPath,
        false,
      )}`;
    }

    if (
      !this.tools.isNil(searchState.careServiceTypeUrlPath) &&
      searchState.careServiceTypeUrlPath !== '' &&
      searchState.careServiceTypeUrlPath !== defaultSearchState.careServiceTypeUrlPath
    ) {
      vardRouteQueryParams[UrlTermsSymbolsEnum.SERVICE_TYPE] = `${this.NormalizeText(
        searchState.careServiceTypeUrlPath,
        false,
      )}`;
    }

    if (
      !this.tools.isNil(searchState.symptomUrlPath) &&
      searchState.symptomUrlPath !== '' &&
      searchState.symptomUrlPath !== defaultSearchState.symptomUrlPath
    ) {
      vardRouteQueryParams[UrlTermsSymbolsEnum.SYMPTOM] = `${this.NormalizeText(searchState.symptomUrlPath, false)}`;
    }

    if (
      !this.tools.isNil(searchState.placeUrlPath) &&
      searchState.placeUrlPath !== '' &&
      searchState.placeUrlPath !== defaultSearchState.placeUrlPath
    ) {
      vardRouteQueryParams[UrlTermsSymbolsEnum.PLACE] = `${this.NormalizeText(searchState.placeUrlPath, false)}`;
    }

    const data: RouterData = {
      baseRouteLink: this.allParametersCombinations(searchState)
        ? this.getVardRouteLink(baseUrl)
        : this.getBaseRouteLink(searchState, baseUrl),
      baseRouteQueryParams: baseRouteQueryParams,
      vardRouteLink: this.getVardRouteLink(baseUrl),
      vardRouteQueryParams: vardRouteQueryParams,
    };

    return data;
  }

  public CreateSearchResultsQueryParams(
    searchState: SearchState,
    fallbackFilterApplied: boolean = false,
    skipVardRouting: boolean = false,
  ): QueryParams {
    const defaultSearchState: SearchState = this.defaults.getDefaultSearchState(
      searchState.caregiverUnitTypeUrlPath,
      searchState.careServiceTypeUrlPath,
      searchState.symptomUrlPath,
      searchState.freeSearchTerm,
    );
    let queryParams: QueryParams = {};
    // EDGE CASE - reset location search when place is selected
    if (searchState.placeUrlPath !== '') {
      searchState.locationSearch = false;
      searchState.lat = 0;
      searchState.lon = 0;
    }

    // EDGE CASE - clear force search results boolean if any other filter is set
    if (
      searchState.forceSearchResults === true &&
      (searchState.caregiverUnitTypeUrlPath !== defaultSearchState.caregiverUnitTypeUrlPath ||
        searchState.placeUrlPath !== defaultSearchState.placeUrlPath ||
        searchState.freeSearchTerm !== defaultSearchState.freeSearchTerm ||
        searchState.page !== defaultSearchState.page ||
        searchState.locationSearch !== defaultSearchState.locationSearch ||
        searchState.openNow !== defaultSearchState.openNow ||
        searchState.openWeekend !== defaultSearchState.openWeekend ||
        searchState.customerFeatureType !== defaultSearchState.customerFeatureType ||
        searchState.timeslot != defaultSearchState.timeslot ||
        (searchState.locationSearch === true && searchState.lat !== defaultSearchState.lat) ||
        (searchState.locationSearch === true && searchState.lon !== defaultSearchState.lon))
    ) {
      searchState.forceSearchResults = false;
    }

    if (
      !this.tools.isNil(searchState.freeSearchTerm) &&
      searchState.freeSearchTerm !== '' &&
      searchState.freeSearchTerm !== defaultSearchState.freeSearchTerm
    ) {
      queryParams[UrlTermsSymbolsEnum.FREE] = searchState.freeSearchTerm;
    }
    if (
      searchState.locationSearch === true &&
      !this.tools.isNil(searchState.lat) &&
      searchState.lat !== 0 &&
      searchState.lat !== defaultSearchState.lat
    ) {
      queryParams[UrlTermsSymbolsEnum.LATITUDE] = searchState.lat;
    }
    if (
      searchState.locationSearch === true &&
      !this.tools.isNil(searchState.lon) &&
      searchState.lon !== 0 &&
      searchState.lon !== defaultSearchState.lon
    ) {
      queryParams[UrlTermsSymbolsEnum.LONGITUDE] = searchState.lon;
    }
    if (!this.tools.isNil(searchState.distance) && searchState.distance !== 0) {
      queryParams[UrlTermsSymbolsEnum.DISTANCE] = searchState.distance;
    }

    if (!this.tools.isNil(searchState.page) && searchState.page !== 0 && searchState.page !== defaultSearchState.page) {
      queryParams[UrlTermsSymbolsEnum.PAGE] = searchState.page;
    }
    if (
      !this.tools.isNil(searchState.openNow) &&
      searchState.openNow &&
      searchState.openNow !== defaultSearchState.openNow
    ) {
      queryParams[UrlTermsSymbolsEnum.OPEN_NOW] = 1;
    }
    if (
      !this.tools.isNil(searchState.openWeekend) &&
      searchState.openWeekend &&
      searchState.openWeekend !== defaultSearchState.openWeekend
    ) {
      queryParams[UrlTermsSymbolsEnum.OPEN_WEEKEND] = 1;
    }

    if (!this.tools.isNil(searchState.timeslot) && searchState.timeslot.length > 0) {
      queryParams[UrlTermsSymbolsEnum.TIMESLOT] = searchState.timeslot.map((slot) => UrlTermsSymbolTimeslotsEnum[slot]);
      if (queryParams[UrlTermsSymbolsEnum.TIMESLOT].length === 0) {
        delete queryParams[UrlTermsSymbolsEnum.TIMESLOT];
      }
    }

    if (searchState.customerFeatureType !== defaultSearchState.customerFeatureType) {
      queryParams[UrlTermsSymbolsEnum.CUSTOMER_FEATURE_TYPE] = searchState.customerFeatureType;
    }

    if (
      searchState.attributes !== undefined &&
      searchState.attributes !== null &&
      searchState.attributes !== defaultSearchState.attributes
    ) {
      queryParams[UrlTermsSymbolsEnum.ATTRIBUTE] = [];
      Object.keys(searchState.attributes).forEach((key) => {
        if (searchState.attributes[key]) {
          queryParams[UrlTermsSymbolsEnum.ATTRIBUTE].push(key);
        }
      });
      if (queryParams[UrlTermsSymbolsEnum.ATTRIBUTE].length === 0) {
        delete queryParams[UrlTermsSymbolsEnum.ATTRIBUTE];
      }
    }

    if (fallbackFilterApplied) {
      queryParams[UrlTermsSymbolsEnum.AFTER_REDIRECT] = '1';
    }

    if (searchState.forceSearchResults === true) {
      queryParams[UrlTermsSymbolsEnum.FORCE_SEARCH_RESULTS] = '1';
    }

    if (searchState.forceContentPage === true) {
      queryParams[UrlTermsSymbolsEnum.FORCE_CONTENT_PAGE] = '1';
    }

    if (searchState.tab !== null && searchState.tab !== defaultSearchState.tab) {
      queryParams[UrlTermsSymbolsEnum.TAB] = searchState.tab;
    }

    if (!this.needUseBaseRoute(searchState, fallbackFilterApplied) && !skipVardRouting) {
      if (
        !this.tools.isNil(searchState.caregiverUnitTypeUrlPath) &&
        searchState.caregiverUnitTypeUrlPath !== '' &&
        searchState.caregiverUnitTypeUrlPath !== defaultSearchState.caregiverUnitTypeUrlPath
      ) {
        queryParams[UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE] = `${this.NormalizeText(
          searchState.caregiverUnitTypeUrlPath,
          false,
        )}`;
      }

      if (
        !this.tools.isNil(searchState.careServiceTypeUrlPath) &&
        searchState.careServiceTypeUrlPath !== '' &&
        searchState.careServiceTypeUrlPath !== defaultSearchState.careServiceTypeUrlPath
      ) {
        queryParams[UrlTermsSymbolsEnum.SERVICE_TYPE] = `${this.NormalizeText(
          searchState.careServiceTypeUrlPath,
          false,
        )}`;
      }

      if (
        !this.tools.isNil(searchState.symptomUrlPath) &&
        searchState.symptomUrlPath !== '' &&
        searchState.symptomUrlPath !== defaultSearchState.symptomUrlPath
      ) {
        queryParams[UrlTermsSymbolsEnum.SYMPTOM] = `${this.NormalizeText(searchState.symptomUrlPath, false)}`;
      }

      if (
        !this.tools.isNil(searchState.placeUrlPath) &&
        searchState.placeUrlPath !== '' &&
        searchState.placeUrlPath !== defaultSearchState.placeUrlPath
      ) {
        queryParams[UrlTermsSymbolsEnum.PLACE] = `${this.NormalizeText(searchState.placeUrlPath, false)}`;
      }
    }

    // check if all parameters combinations are empty
    if (
      (this.tools.isNil(searchState.caregiverUnitTypeUrlPath) || searchState.caregiverUnitTypeUrlPath === '') &&
      (this.tools.isNil(searchState.careServiceTypeUrlPath) || searchState.careServiceTypeUrlPath === '') &&
      (this.tools.isNil(searchState.symptomUrlPath) || searchState.symptomUrlPath === '') &&
      (this.tools.isNil(searchState.placeUrlPath) || searchState.placeUrlPath === '') &&
      (this.tools.isNil(searchState.freeSearchTerm) || searchState.freeSearchTerm === '') &&
      searchState.locationSearch === false
    ) {
      queryParams = {};
    }

    // check if we have all search parameters are filled
    if (this.allParametersCombinations(searchState)) {
      queryParams[UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE] = `${this.NormalizeText(
        searchState.caregiverUnitTypeUrlPath,
        false,
      )}`;
      queryParams[UrlTermsSymbolsEnum.SERVICE_TYPE] = `${this.NormalizeText(
        searchState.careServiceTypeUrlPath,
        false,
      )}`;
      queryParams[UrlTermsSymbolsEnum.SYMPTOM] = `${this.NormalizeText(searchState.symptomUrlPath, false)}`;
      queryParams[UrlTermsSymbolsEnum.PLACE] = `${this.NormalizeText(searchState.placeUrlPath, false)}`;
    }
    queryParams = this.cleanQueryParams(queryParams);
    queryParams = this.sortQueryParams(queryParams);
    return queryParams;
  }

  public CreateCareproviderUnitRouterLink(id: number, urlPath: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.CAREPROVIDER);
    routerLinkTab.push(`${id}-${urlPath}`);
    return routerLinkTab;
  }

  public CreateCareproviderUnitRouterLinkFromString(url: string): string[] {
    const routerLinkTab: string[] = url.split('/');
    routerLinkTab.unshift('/');
    return routerLinkTab;
  }

  public CreateCareproviderLandingPageRouterLinkFromString(url: string): string[] {
    const routerLinkTab: string[] = url.split('/');
    routerLinkTab.unshift('/');
    return routerLinkTab;
  }

  public CreateCareServiceRouterLink(
    id: number,
    urlPath: string,
    careServiceId: number,
    careServiceUrlPath: string,
  ): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.CARESERVICE);
    routerLinkTab.push(`${id}-${urlPath}`);
    routerLinkTab.push(`${careServiceId}-${careServiceUrlPath}`);
    return routerLinkTab;
  }

  public CreateCareProviderUnitQueryParams(isPreview: boolean): QueryParams {
    let queryParams: QueryParams = {};
    if (isPreview === true) {
      queryParams[RoutesQueryParamsEnum.PREVIEW] = '1';
    }
    return queryParams;
  }

  public CreateBlogPostRouterLink(id: number, urlPath: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.BLOG);
    routerLinkTab.push(`${id.toString()}-${this.NormalizeText(urlPath, true)}`);
    return routerLinkTab;
  }

  public CreateBlogArchiveRouterLink(year: number, month: number): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.BLOG);
    routerLinkTab.push(RoutesEnums.ARCHIVE);
    routerLinkTab.push(`${year.toString()}-${month.toString()}`);
    return routerLinkTab;
  }

  public CreateBlogTagRouterLink(id: number, urlPath: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.BLOG);
    routerLinkTab.push(RoutesEnums.TAG);
    routerLinkTab.push(`${id.toString()}-${this.NormalizeText(urlPath, true)}`);
    return routerLinkTab;
  }

  public CreateBlogRouterLink(): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.BLOG);
    return routerLinkTab;
  }

  public CreateNativesRouterLink(): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.NATIVES);
    return routerLinkTab;
  }

  public CreateNativePostRouterLink(id: number, urlPath: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.NATIVES);
    routerLinkTab.push(`${id.toString()}-${this.NormalizeText(urlPath, true)}`);
    return routerLinkTab;
  }

  public CreateStaticPageRouterLink(page: string, child?: string, template?: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    if (template !== PageTemplateEnum.FOR_CAREPROVIDERS) {
      routerLinkTab.push(RoutesEnums.PAGE);
    }
    routerLinkTab.push(this.NormalizeText(page, true));
    if (!this.tools.isNil(child)) {
      routerLinkTab.push(this.NormalizeText(child, true));
    }
    return routerLinkTab;
  }

  public CreateForCareproviderRouterLink(urlPath?: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.FOR_CAREPROVIDERS);
    if (!this.tools.isNil(urlPath)) {
      routerLinkTab.push(urlPath);
    }
    return routerLinkTab;
  }

  public CreateAuthorRouterLink(urlPath: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.AUTHOR);
    routerLinkTab.push(this.NormalizeText(urlPath, true));
    return routerLinkTab;
  }

  public CreateMyPagesRouterLink(): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.MYPAGES);
    return routerLinkTab;
  }

  public CreateMyPagesBookingCancellationRouterLink(bookingId: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.MYPAGES);
    routerLinkTab.push(MyPagesRoutesEnum.BOOKING_CANCELLATION);
    routerLinkTab.push(bookingId);
    return routerLinkTab;
  }

  public CreateLoginRouterLink(): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.AUTH);
    routerLinkTab.push(RoutesEnums.AUTH_LOGIN);
    return routerLinkTab;
  }

  public CreateLogoutRouterLink(): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    routerLinkTab.push(RoutesEnums.AUTH);
    routerLinkTab.push(RoutesEnums.AUTH_LOGOUT);
    return routerLinkTab;
  }

  public CreateInternalBookingRouterLink(id: number, serviceId?: number, timeslot?: string): string[] {
    const routerLinkTab: string[] = [];
    routerLinkTab.push('/');
    if (id && id > 0) {
      routerLinkTab.push(RoutesEnums.BOOKING);
      routerLinkTab.push(id.toString());
      if (!this.tools.isNil(serviceId)) {
        routerLinkTab.push(serviceId.toString());
      }
      if (!this.tools.isNil(timeslot)) {
        routerLinkTab.push(timeslot.toString());
      }
    }
    return routerLinkTab;
  }

  public CreateInternalBookingQueryParams(referral: string): QueryParams {
    let queryParams: QueryParams = {};
    if (!this.tools.isNil(referral) && referral !== '') {
      queryParams[RoutesQueryParamsEnum.BOOKING_REFERRAL] = encodeURI(referral);
    }
    return queryParams;
  }

  public NormalizeText(text: string, removeDiactric: boolean, noEmptyStrings?: boolean): string {
    if (this.tools.isNil(text)) {
      return noEmptyStrings === true ? 'saknas' : '';
    }
    let newText = text.toLowerCase().replace(/[^a-z0-9åäö]/g, '-');
    // .replace(/-+/g, '-'); // removed because of prerender redirect loop, VS-4111

    if (removeDiactric === true) {
      newText = newText.replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o');
    }
    return newText;
  }

  public cleanQueryParams(queryParams: QueryParams): QueryParams {
    return Object.fromEntries(Object.entries(queryParams).filter(([key, value]) => value != null && value !== ''));
  }

  public sortQueryParams(queryParams: QueryParams): QueryParams {
    return Object.fromEntries(Object.entries(queryParams).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)));
  }

  private allParametersCombinations(searchState: SearchState): boolean {
    // All parameter combination is a combination of a -p and any two of -c, -t and -s.
    const placePresent = !this.tools.isNil(searchState.placeUrlPath) && searchState.placeUrlPath !== '';
    if (!placePresent) {
      return false;
    }
    const cpruTypePresent =
      !this.tools.isNil(searchState.caregiverUnitTypeUrlPath) && searchState.caregiverUnitTypeUrlPath !== '';
    const csTypePresent =
      !this.tools.isNil(searchState.careServiceTypeUrlPath) && searchState.careServiceTypeUrlPath !== '';
    const symptomPresent = !this.tools.isNil(searchState.symptomUrlPath) && searchState.symptomUrlPath !== '';
    return [cpruTypePresent, csTypePresent, symptomPresent].filter((x) => x).length >= 2;
  }

  private getBaseRouteLink(searchState: SearchState, baseUrl: string[]): string[] {
    let routerLinkTab: string[] = [];

    baseUrl.forEach((urlItem) => {
      routerLinkTab.push(urlItem);
    });

    const defaultSearchState: SearchState = this.defaults.getDefaultSearchState();

    if (
      !this.tools.isNil(searchState.caregiverUnitTypeUrlPath) &&
      searchState.caregiverUnitTypeUrlPath !== '' &&
      searchState.caregiverUnitTypeUrlPath !== defaultSearchState.caregiverUnitTypeUrlPath
    ) {
      routerLinkTab.push(
        `${this.NormalizeText(searchState.caregiverUnitTypeUrlPath, false)}-${
          UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE
        }`,
      );
    }
    if (
      !this.tools.isNil(searchState.careServiceTypeUrlPath) &&
      searchState.careServiceTypeUrlPath !== '' &&
      searchState.careServiceTypeUrlPath !== defaultSearchState.careServiceTypeUrlPath
    ) {
      routerLinkTab.push(
        `${this.NormalizeText(searchState.careServiceTypeUrlPath, false)}-${UrlTermsSymbolsEnum.SERVICE_TYPE}`,
      );
    }
    if (
      !this.tools.isNil(searchState.symptomUrlPath) &&
      searchState.symptomUrlPath !== '' &&
      searchState.symptomUrlPath !== defaultSearchState.symptomUrlPath
    ) {
      routerLinkTab.push(`${this.NormalizeText(searchState.symptomUrlPath, false)}-${UrlTermsSymbolsEnum.SYMPTOM}`);
    }
    if (
      !this.tools.isNil(searchState.placeUrlPath) &&
      searchState.placeUrlPath !== '' &&
      searchState.placeUrlPath !== defaultSearchState.placeUrlPath
    ) {
      routerLinkTab.push(`${this.NormalizeText(searchState.placeUrlPath, false)}-${UrlTermsSymbolsEnum.PLACE}`);
    }

    if (
      routerLinkTab.length === baseUrl.length &&
      searchState.freeSearchTerm === '' &&
      searchState.locationSearch === false
    ) {
      routerLinkTab = ['/'];
    }

    return routerLinkTab;
  }

  private getVardRouteLink(baseUrl: string[]): string[] {
    let routerLinkTab: string[] = [];

    baseUrl.forEach((urlItem) => {
      routerLinkTab.push(urlItem);
    });

    routerLinkTab.push(RoutesEnums.SEARCH_VARDGIVARE);

    return routerLinkTab;
  }

  private needUseBaseRoute(searchState: SearchState, fallbackFilterApplied: boolean = false): boolean {
    const defaultSearchState: SearchState = this.defaults.getDefaultSearchState(
      searchState.caregiverUnitTypeUrlPath,
      searchState.careServiceTypeUrlPath,
      searchState.symptomUrlPath,
      searchState.freeSearchTerm,
    );

    if (
      !this.tools.isNil(searchState.freeSearchTerm) &&
      searchState.freeSearchTerm !== '' &&
      searchState.freeSearchTerm !== defaultSearchState.freeSearchTerm
    ) {
      return false;
    }
    if (!this.tools.isNil(searchState.lat) && searchState.lat !== 0 && searchState.lat !== defaultSearchState.lat) {
      return false;
    }
    if (!this.tools.isNil(searchState.lon) && searchState.lon !== 0 && searchState.lon !== defaultSearchState.lon) {
      return false;
    }

    if (!this.tools.isNil(searchState.page) && searchState.page !== 0 && searchState.page !== defaultSearchState.page) {
      return false;
    }
    if (
      !this.tools.isNil(searchState.openNow) &&
      searchState.openNow &&
      searchState.openNow !== defaultSearchState.openNow
    ) {
      return false;
    }
    if (
      !this.tools.isNil(searchState.openWeekend) &&
      searchState.openWeekend &&
      searchState.openWeekend !== defaultSearchState.openWeekend
    ) {
      return false;
    }

    if (!this.tools.isNil(searchState.timeslot)) {
      return false;
    }

    if (searchState.customerFeatureType !== defaultSearchState.customerFeatureType) {
      return false;
    }

    if (fallbackFilterApplied) {
      return false;
    }

    let numberOfRouteParams = 0;
    if (!this.tools.isNil(searchState.caregiverUnitTypeUrlPath) && searchState.caregiverUnitTypeUrlPath !== '') {
      numberOfRouteParams++;
    }
    if (!this.tools.isNil(searchState.careServiceTypeUrlPath) && searchState.careServiceTypeUrlPath !== '') {
      numberOfRouteParams++;
    }
    if (!this.tools.isNil(searchState.symptomUrlPath) && searchState.symptomUrlPath !== '') {
      numberOfRouteParams++;
    }

    return numberOfRouteParams < 2;
  }
}
